import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { buttonStyleToCss } from "@/lib/kioskGrid";

const FONT_FAMILIES = ["Montserrat", "Inter", "Poppins", "Lato", "Raleway", "Oswald", "Playfair Display", "Cormorant Garamond", "Merriweather", "Roboto Slab"];
const WEIGHTS = ["300", "400", "500", "600", "700", "800", "900"];
const SHADOWS = ["none", "sm", "md", "lg", "xl"];

export function ButtonStyleEditor({ value, onChange, testidPrefix = "btn-style", previewLabel = "Preview button" }) {
  const v = value || {};
  const font = v.font || {};
  const set = (patch) => onChange({ ...v, ...patch });
  const setFont = (patch) => onChange({ ...v, font: { ...font, ...patch } });

  return (
    <div className="rounded-xl border border-[#E5E2DC] bg-[#FAF9F6] p-4 space-y-4" data-testid={testidPrefix}>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Background</Label>
            <Input type="color" value={v.bg_color || "#2A2626"} onChange={(e) => set({ bg_color: e.target.value })} className="h-9 p-1" data-testid={`${testidPrefix}-bg`} />
          </div>
          <div>
            <Label className="text-xs">Text color</Label>
            <Input type="color" value={v.text_color || "#FFFFFF"} onChange={(e) => set({ text_color: e.target.value })} className="h-9 p-1" data-testid={`${testidPrefix}-text`} />
          </div>
          <div>
            <Label className="text-xs">Corner radius: {v.border_radius ?? 16}px</Label>
            <Slider min={0} max={60} step={1} value={[v.border_radius ?? 16]} onValueChange={(x) => set({ border_radius: x[0] })} />
          </div>
        </div>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Shadow</Label>
            <Select value={v.shadow || "lg"} onValueChange={(x) => set({ shadow: x })}>
              <SelectTrigger className="h-9" data-testid={`${testidPrefix}-shadow`}><SelectValue /></SelectTrigger>
              <SelectContent>{SHADOWS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Padding Y: {v.padding_y ?? 24}px</Label>
            <Slider min={8} max={60} step={1} value={[v.padding_y ?? 24]} onValueChange={(x) => set({ padding_y: x[0] })} />
          </div>
          <div>
            <Label className="text-xs">Padding X: {v.padding_x ?? 32}px</Label>
            <Slider min={8} max={80} step={1} value={[v.padding_x ?? 32]} onValueChange={(x) => set({ padding_x: x[0] })} />
          </div>
        </div>
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        <div>
          <Label className="text-xs">Font</Label>
          <Select value={font.family || "Montserrat"} onValueChange={(x) => setFont({ family: x })}>
            <SelectTrigger className="h-9" data-testid={`${testidPrefix}-font`}><SelectValue /></SelectTrigger>
            <SelectContent>{FONT_FAMILIES.map((f) => <SelectItem key={f} value={f} style={{ fontFamily: `'${f}', sans-serif` }}>{f}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Weight</Label>
          <Select value={font.weight || "700"} onValueChange={(x) => setFont({ weight: x })}>
            <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
            <SelectContent>{WEIGHTS.map((w) => <SelectItem key={w} value={w}>{w}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Size: {font.size ?? 22}px</Label>
          <Slider min={12} max={48} step={1} value={[font.size ?? 22]} onValueChange={(x) => setFont({ size: x[0] })} />
        </div>
      </div>
      <div className="pt-1">
        <p className="text-[10px] uppercase tracking-wider text-[#5C5856] mb-2">Preview</p>
        <button style={buttonStyleToCss(v)} data-testid={`${testidPrefix}-preview`}>{previewLabel}</button>
      </div>
    </div>
  );
}
